import { useState } from "react";
import { Edit, Trash2, MessageSquare, ChevronRight, Layers } from "lucide-react";
import EditCategoryModal from "./EditCategoryModal";
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftBadge from "../../../components/SoftBadge";
import SoftButton from "../../../components/SoftButton";
import PropTypes from "prop-types";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";

const CategoryCard = ({ category, language, onEdit, onDelete, onViewQuestions }) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const categoryName = language === "en" ? category.name.en : category.name.ar;

    return (
        <>
            <SoftBox
                borderRadius="lg"
                boxShadow="0px 4px 20px rgba(0, 0, 0, 0.1)"
                p={2}
                transition="all 0.3s ease"
                cursor="pointer"
                bgColor="white"
                sx={{
                    '&:hover': {
                        boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-2px)',
                    },
                }}
            >
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftBox display="flex" alignItems="center" gap={1.5}>
                        <SoftBox
                            width={40}
                            height={40}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgColor="primary"
                        >
                            <Layers className="h-5 w-5 text-primary" color={"white"}/>
                        </SoftBox>
                        <SoftBox>
                            <SoftTypography variant="h6" fontWeight="bold" color="dark">
                                {categoryName}
                            </SoftTypography>
                            <SoftBox display="flex" alignItems="center" gap={1} mt={0.5}>
                                <SoftBadge color="secondary" size="sm" sx={{ fontSize: '0.9rem' }}>
                                    {category.questionCount} questions
                                </SoftBadge>
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>

                    <SoftButton
                        variant="text"
                        color="dark"
                        size="small"
                        sx={{ minWidth: "auto", padding: 1.5 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewQuestions(category);
                        }}
                    >
                        <ChevronRight className="h-5 w-5 text-muted" />
                    </SoftButton>
                </SoftBox>

                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    <SoftButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<MessageSquare className="h-4 w-4" />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewQuestions(category);
                        }}
                        sx={{
                            color: "#344767",
                            borderColor: "#f0f0f0",
                            '&:hover': {
                                backgroundColor: "#f9f9f9",
                                borderColor: "#e0e0e0",
                            },
                        }}
                    >
                        Manage Questions
                    </SoftButton>

                    <SoftBox display="flex" gap={1}>
                        <SoftButton
                            variant="text"
                            color="dark"
                            size="small"
                            sx={{ minWidth: "auto", padding: 1.5 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditModalOpen(true);
                            }}
                        >
                            <Edit className="h-4 w-4" />
                        </SoftButton>

                        <SoftButton
                            variant="text"
                            color="error"
                            size="small"
                            sx={{ minWidth: "auto", padding: 1.5 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsDeleteDialogOpen(true);
                            }}
                        >
                            <Trash2 className="h-4 w-4" />
                        </SoftButton>
                    </SoftBox>
                </SoftBox>
            </SoftBox>

            <EditCategoryModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                category={category}
                onSave={onEdit}
            />

            <ConfirmDeleteDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={() => onDelete(category.id)}
                title="Delete Category"
                description="Are you sure you want to delete this category? All questions in this category will also be permanently deleted."
                itemName={categoryName}
            />
        </>
    );
};


CategoryCard.propTypes = {
    category: PropTypes.object,
    language: PropTypes.string,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onViewQuestions: PropTypes.func,
};

export default CategoryCard;
